import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ActionButton, ActionLink } from "@wine2trace/ui/FormComponents";
import { selectActiveWinery } from "../../context/selectors";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useLabels from "../../hooks/useLabels";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../routeAccess";
import { canPlanOperation } from "../../operationAccess";
import { getPDF } from "../../services/utils";

// Operation types that can be programmed from here; the segment is the /programming/:type/new route.
const OPERATION_TYPES = [
  "DESTEMMING",
  "CUT",
  "RACKING",
  "FILTERING",
  "OUILLAGE",
  "TREATMENT",
  "PHYSICAL",
  "ANALYSIS",
  "DECANT",
  "WINEMAKING",
  "WINEMAKINGPOST",
  "BOTTLING",
  "CLEANING",
];

// "Azioni rapide": shortcuts to the creation flows, each shown to who holds its permission in the winery: the
// operations the user can plan (OPERATIONS write, or its grant scoped to the type: the /programming/:type/new
// route and the server ask the same), lots (LOTS write) and the daily report (REPORTS export).
const HomeQuickActions = () => {
  const [getLabel] = useLabels();
  const activeWinery = useSelector(selectActiveWinery);
  const axiosPrivate = useAxiosPrivate();
  const { can } = usePermissions();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const programmableTypes = OPERATION_TYPES.filter((type) => canPlanOperation(can, type));
  const canProgram = programmableTypes.length > 0;
  const canCreateLot = can(...PERMISSIONS.lotsWrite);
  const canReport = can(...PERMISSIONS.reportsExport);
  if (!canProgram && !canCreateLot && !canReport) return null;

  const downloadDailyReport = async () => {
    const data = {
      pdf_type: "dailyReport",
      type: "operations",
      title: `${getLabel("printDailyExecutedOperation")} - ${new Date().toLocaleDateString("it-IT")}`,
      rows: [activeWinery?.id],
      columns: [
        getLabel("operationType"),
        getLabel("operationSrcTank"),
        getLabel("operationDestTank"),
        getLabel("operationCompletionUser"),
        getLabel("operationCompetionDate"),
      ],
      headers: ["type", "src_tanks", "dest_tanks", "completed_by", "completion_date"],
      winery_name: activeWinery?.name || "",
      order_by: "expire_date",
      order_sort: "desc",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.languages?.length > 0 ? navigator.languages[0]?.replace("_", "-") : "it-IT",
    };

    const isDone = await getPDF(data, axiosPrivate, getLabel("pdfType_DAILY_REPORT"));
    if (!isDone) toast.error(getLabel("toast_errorGeneric"), { position: toast.POSITION.BOTTOM_RIGHT });
  };

  const programLabel = getLabel("programOperation");

  return (
    <section className="home_panel home_actions_panel" aria-label={getLabel("homeQuickActionsTitle")}>
      <h4 className="home_panel_title">{getLabel("homeQuickActionsTitle")}</h4>
      <div className="home_quick_actions">
        {canProgram && (
          <div className="home_quick_actions_menu">
            <ActionButton
              variant="block"
              icon="fa-calendar-plus"
              aria-expanded={isMenuOpen}
              aria-controls="home-operation-types"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              {programLabel}
            </ActionButton>
            {isMenuOpen && (
              <ul
                id="home-operation-types"
                className="home_quick_actions_list"
                aria-label={programLabel}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setIsMenuOpen(false);
                }}
              >
                {programmableTypes.map((type) => (
                  <li key={type}>
                    <ActionLink to={`/programming/${type.toLowerCase()}/new`} variant="menu">
                      {getLabel(`operationType${type}`)}
                    </ActionLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {canCreateLot && (
          <ActionButton to="/lots/new" variant="block" icon="fa-plus">
            {getLabel("createLot")}
          </ActionButton>
        )}
        {canReport && (
          <ActionButton variant="block" icon="fa-print" onClick={downloadDailyReport}>
            {getLabel("printDailyExecutedOperation")}
          </ActionButton>
        )}
      </div>
    </section>
  );
};

export default HomeQuickActions;
